// frontend/src/reducers/adminOrderReducer.js

import * as actionTypes from "../constants/adminOrderConstants";

function createAsyncState(extra = {}) {
  return {
    loading: false,
    success: false,
    error: null,
    errorCode: "",
    requestId: "",
    retryAfterSeconds: 0,
    message: "",
    ...extra,
  };
}

function createInitialState() {
  return {
    list: createAsyncState({
      orders: [],
      page: 1,
      limit: 20,
      pages: 1,
      count: 0,
    }),

    details: createAsyncState({
      order: null,
    }),

    analytics: createAsyncState({
      totals: null,
      orderStatusBreakdown: [],
      paymentStatusBreakdown: [],
    }),

    updateStatus: createAsyncState({
      order: null,
    }),

    updateFulfillment: createAsyncState({
      order: null,
    }),

    updateCutStatus: createAsyncState({
      order: null,
    }),

    manualPayment: createAsyncState({
      order: null,
    }),

    refundOrder: createAsyncState({
      order: null,
      refundAction: null,
    }),

    returnList: createAsyncState({
      returnRequests: [],
      page: 1,
      limit: 20,
      pages: 1,
      count: 0,
    }),

    returnDetails: createAsyncState({
      returnRequest: null,
    }),

    approveReturn: createAsyncState({
      returnRequest: null,
    }),

    rejectReturn: createAsyncState({
      returnRequest: null,
    }),

    markReceived: createAsyncState({
      returnRequest: null,
    }),

    refundReturn: createAsyncState({
      returnRequest: null,
      refundAction: null,
    }),
  };
}

const initialState = createInitialState();

function requestBranch(branch = {}) {
  return {
    ...branch,
    loading: true,
    success: false,
    error: null,
    errorCode: "",
    requestId: "",
    retryAfterSeconds: 0,
    message: "",
  };
}

function successBranch(branch = {}, payload = {}, extra = {}) {
  return {
    ...branch,
    loading: false,
    success: true,
    error: null,
    errorCode: "",
    requestId: "",
    retryAfterSeconds: 0,
    message: String(payload?.message || ""),
    ...extra,
  };
}

function failBranch(branch = {}, payload = {}) {
  return {
    ...branch,
    loading: false,
    success: false,
    error: String(payload?.message || "Request failed."),
    errorCode: String(payload?.code || ""),
    requestId: String(payload?.requestId || ""),
    retryAfterSeconds: Number(payload?.retryAfterSeconds || 0),
    message: "",
  };
}

function getOrder(payload = {}) {
  return payload?.order || null;
}

function getReturnRequest(payload = {}) {
  return payload?.returnRequest || null;
}

function getOrderIdentity(order = {}) {
  return String(order?.orderNumber || order?._id || order?.id || "").trim();
}

function mergeUpdatedOrder(state = {}, order = null) {
  if (!order) return state;

  const updatedIdentity = getOrderIdentity(order);
  const currentDetailsIdentity = getOrderIdentity(state.details?.order);

  return {
    ...state,
    list: {
      ...state.list,
      orders: Array.isArray(state.list?.orders)
        ? state.list.orders.map((item) =>
            getOrderIdentity(item) === updatedIdentity ? order : item,
          )
        : [],
    },
    details: {
      ...state.details,
      order:
        updatedIdentity && updatedIdentity === currentDetailsIdentity
          ? order
          : state.details?.order || null,
    },
  };
}

export function adminOrderReducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.ADMIN_ORDER_LIST_REQUEST:
      return {
        ...state,
        list: requestBranch(state.list),
      };

    case actionTypes.ADMIN_ORDER_LIST_SUCCESS:
      return {
        ...state,
        list: successBranch(state.list, action.payload, {
          orders: Array.isArray(action.payload?.orders)
            ? action.payload.orders
            : [],
          page: Number(action.payload?.page || 1),
          limit: Number(action.payload?.limit || 20),
          pages: Number(action.payload?.pages || 1),
          count: Number(action.payload?.count || 0),
        }),
      };

    case actionTypes.ADMIN_ORDER_LIST_FAIL:
      return {
        ...state,
        list: failBranch(state.list, action.payload),
      };

    case actionTypes.ADMIN_ORDER_LIST_RESET:
      return {
        ...state,
        list: initialState.list,
      };

    case actionTypes.ADMIN_ORDER_DETAILS_REQUEST:
      return {
        ...state,
        details: requestBranch(state.details),
      };

    case actionTypes.ADMIN_ORDER_DETAILS_SUCCESS:
      return {
        ...state,
        details: successBranch(state.details, action.payload, {
          order: getOrder(action.payload),
        }),
      };

    case actionTypes.ADMIN_ORDER_DETAILS_FAIL:
      return {
        ...state,
        details: failBranch(state.details, action.payload),
      };

    case actionTypes.ADMIN_ORDER_DETAILS_RESET:
      return {
        ...state,
        details: initialState.details,
      };

    case actionTypes.ADMIN_ORDER_ANALYTICS_REQUEST:
      return {
        ...state,
        analytics: requestBranch(state.analytics),
      };

    case actionTypes.ADMIN_ORDER_ANALYTICS_SUCCESS:
      return {
        ...state,
        analytics: successBranch(state.analytics, action.payload, {
          totals: action.payload?.totals || null,
          orderStatusBreakdown: Array.isArray(
            action.payload?.orderStatusBreakdown,
          )
            ? action.payload.orderStatusBreakdown
            : [],
          paymentStatusBreakdown: Array.isArray(
            action.payload?.paymentStatusBreakdown,
          )
            ? action.payload.paymentStatusBreakdown
            : [],
        }),
      };

    case actionTypes.ADMIN_ORDER_ANALYTICS_FAIL:
      return {
        ...state,
        analytics: failBranch(state.analytics, action.payload),
      };

    case actionTypes.ADMIN_ORDER_ANALYTICS_RESET:
      return {
        ...state,
        analytics: initialState.analytics,
      };

    case actionTypes.ADMIN_ORDER_STATUS_UPDATE_REQUEST:
      return {
        ...state,
        updateStatus: requestBranch(state.updateStatus),
      };

    case actionTypes.ADMIN_ORDER_STATUS_UPDATE_SUCCESS: {
      const updatedOrder = getOrder(action.payload);

      return mergeUpdatedOrder(
        {
          ...state,
          updateStatus: successBranch(state.updateStatus, action.payload, {
            order: updatedOrder,
          }),
        },
        updatedOrder,
      );
    }

    case actionTypes.ADMIN_ORDER_STATUS_UPDATE_FAIL:
      return {
        ...state,
        updateStatus: failBranch(state.updateStatus, action.payload),
      };

    case actionTypes.ADMIN_ORDER_STATUS_UPDATE_RESET:
      return {
        ...state,
        updateStatus: initialState.updateStatus,
      };

    case actionTypes.ADMIN_ORDER_FULFILLMENT_UPDATE_REQUEST:
      return {
        ...state,
        updateFulfillment: requestBranch(state.updateFulfillment),
      };

    case actionTypes.ADMIN_ORDER_FULFILLMENT_UPDATE_SUCCESS: {
      const updatedOrder = getOrder(action.payload);

      return mergeUpdatedOrder(
        {
          ...state,
          updateFulfillment: successBranch(
            state.updateFulfillment,
            action.payload,
            {
              order: updatedOrder,
            },
          ),
        },
        updatedOrder,
      );
    }

    case actionTypes.ADMIN_ORDER_FULFILLMENT_UPDATE_FAIL:
      return {
        ...state,
        updateFulfillment: failBranch(state.updateFulfillment, action.payload),
      };

    case actionTypes.ADMIN_ORDER_FULFILLMENT_UPDATE_RESET:
      return {
        ...state,
        updateFulfillment: initialState.updateFulfillment,
      };

    case actionTypes.ADMIN_ORDER_CUT_STATUS_UPDATE_REQUEST:
      return {
        ...state,
        updateCutStatus: requestBranch(state.updateCutStatus),
      };

    case actionTypes.ADMIN_ORDER_CUT_STATUS_UPDATE_SUCCESS: {
      const updatedOrder = getOrder(action.payload);

      return mergeUpdatedOrder(
        {
          ...state,
          updateCutStatus: successBranch(
            state.updateCutStatus,
            action.payload,
            {
              order: updatedOrder,
            },
          ),
        },
        updatedOrder,
      );
    }

    case actionTypes.ADMIN_ORDER_CUT_STATUS_UPDATE_FAIL:
      return {
        ...state,
        updateCutStatus: failBranch(state.updateCutStatus, action.payload),
      };

    case actionTypes.ADMIN_ORDER_CUT_STATUS_UPDATE_RESET:
      return {
        ...state,
        updateCutStatus: initialState.updateCutStatus,
      };

    case actionTypes.ADMIN_ORDER_MANUAL_PAYMENT_UPDATE_REQUEST:
      return {
        ...state,
        manualPayment: requestBranch(state.manualPayment),
      };

    case actionTypes.ADMIN_ORDER_MANUAL_PAYMENT_UPDATE_SUCCESS: {
      const updatedOrder = getOrder(action.payload);

      return mergeUpdatedOrder(
        {
          ...state,
          manualPayment: successBranch(state.manualPayment, action.payload, {
            order: updatedOrder,
          }),
        },
        updatedOrder,
      );
    }

    case actionTypes.ADMIN_ORDER_MANUAL_PAYMENT_UPDATE_FAIL:
      return {
        ...state,
        manualPayment: failBranch(state.manualPayment, action.payload),
      };

    case actionTypes.ADMIN_ORDER_MANUAL_PAYMENT_UPDATE_RESET:
      return {
        ...state,
        manualPayment: initialState.manualPayment,
      };

    case actionTypes.ADMIN_ORDER_REFUND_REQUEST:
      return {
        ...state,
        refundOrder: requestBranch(state.refundOrder),
      };

    case actionTypes.ADMIN_ORDER_REFUND_SUCCESS: {
      const updatedOrder = getOrder(action.payload);

      return mergeUpdatedOrder(
        {
          ...state,
          refundOrder: successBranch(state.refundOrder, action.payload, {
            order: updatedOrder,
            refundAction: action.payload?.refundAction || null,
          }),
        },
        updatedOrder,
      );
    }

    case actionTypes.ADMIN_ORDER_REFUND_FAIL:
      return {
        ...state,
        refundOrder: failBranch(state.refundOrder, action.payload),
      };

    case actionTypes.ADMIN_ORDER_REFUND_RESET:
      return {
        ...state,
        refundOrder: initialState.refundOrder,
      };

    case actionTypes.ADMIN_RETURN_LIST_REQUEST:
      return {
        ...state,
        returnList: requestBranch(state.returnList),
      };

    case actionTypes.ADMIN_RETURN_LIST_SUCCESS:
      return {
        ...state,
        returnList: successBranch(state.returnList, action.payload, {
          returnRequests: Array.isArray(action.payload?.returnRequests)
            ? action.payload.returnRequests
            : [],
          page: Number(action.payload?.page || 1),
          limit: Number(action.payload?.limit || 20),
          pages: Number(action.payload?.pages || 1),
          count: Number(action.payload?.count || 0),
        }),
      };

    case actionTypes.ADMIN_RETURN_LIST_FAIL:
      return {
        ...state,
        returnList: failBranch(state.returnList, action.payload),
      };

    case actionTypes.ADMIN_RETURN_LIST_RESET:
      return {
        ...state,
        returnList: initialState.returnList,
      };

    case actionTypes.ADMIN_RETURN_DETAILS_REQUEST:
      return {
        ...state,
        returnDetails: requestBranch(state.returnDetails),
      };

    case actionTypes.ADMIN_RETURN_DETAILS_SUCCESS:
      return {
        ...state,
        returnDetails: successBranch(state.returnDetails, action.payload, {
          returnRequest: getReturnRequest(action.payload),
        }),
      };

    case actionTypes.ADMIN_RETURN_DETAILS_FAIL:
      return {
        ...state,
        returnDetails: failBranch(state.returnDetails, action.payload),
      };

    case actionTypes.ADMIN_RETURN_DETAILS_RESET:
      return {
        ...state,
        returnDetails: initialState.returnDetails,
      };

    case actionTypes.ADMIN_RETURN_APPROVE_REQUEST:
      return {
        ...state,
        approveReturn: requestBranch(state.approveReturn),
      };

    case actionTypes.ADMIN_RETURN_APPROVE_SUCCESS:
      return {
        ...state,
        approveReturn: successBranch(state.approveReturn, action.payload, {
          returnRequest: getReturnRequest(action.payload),
        }),
      };

    case actionTypes.ADMIN_RETURN_APPROVE_FAIL:
      return {
        ...state,
        approveReturn: failBranch(state.approveReturn, action.payload),
      };

    case actionTypes.ADMIN_RETURN_APPROVE_RESET:
      return {
        ...state,
        approveReturn: initialState.approveReturn,
      };

    case actionTypes.ADMIN_RETURN_REJECT_REQUEST:
      return {
        ...state,
        rejectReturn: requestBranch(state.rejectReturn),
      };

    case actionTypes.ADMIN_RETURN_REJECT_SUCCESS:
      return {
        ...state,
        rejectReturn: successBranch(state.rejectReturn, action.payload, {
          returnRequest: getReturnRequest(action.payload),
        }),
      };

    case actionTypes.ADMIN_RETURN_REJECT_FAIL:
      return {
        ...state,
        rejectReturn: failBranch(state.rejectReturn, action.payload),
      };

    case actionTypes.ADMIN_RETURN_REJECT_RESET:
      return {
        ...state,
        rejectReturn: initialState.rejectReturn,
      };

    case actionTypes.ADMIN_RETURN_RECEIVED_REQUEST:
      return {
        ...state,
        markReceived: requestBranch(state.markReceived),
      };

    case actionTypes.ADMIN_RETURN_RECEIVED_SUCCESS:
      return {
        ...state,
        markReceived: successBranch(state.markReceived, action.payload, {
          returnRequest: getReturnRequest(action.payload),
        }),
      };

    case actionTypes.ADMIN_RETURN_RECEIVED_FAIL:
      return {
        ...state,
        markReceived: failBranch(state.markReceived, action.payload),
      };

    case actionTypes.ADMIN_RETURN_RECEIVED_RESET:
      return {
        ...state,
        markReceived: initialState.markReceived,
      };

    case actionTypes.ADMIN_RETURN_REFUND_REQUEST:
      return {
        ...state,
        refundReturn: requestBranch(state.refundReturn),
      };

    case actionTypes.ADMIN_RETURN_REFUND_SUCCESS:
      return {
        ...state,
        refundReturn: successBranch(state.refundReturn, action.payload, {
          returnRequest: getReturnRequest(action.payload),
          refundAction: action.payload?.refundAction || null,
        }),
      };

    case actionTypes.ADMIN_RETURN_REFUND_FAIL:
      return {
        ...state,
        refundReturn: failBranch(state.refundReturn, action.payload),
      };

    case actionTypes.ADMIN_RETURN_REFUND_RESET:
      return {
        ...state,
        refundReturn: initialState.refundReturn,
      };

    case actionTypes.ADMIN_ORDER_RESET_ERROR:
      return {
        ...state,
        list: { ...state.list, error: null, errorCode: "" },
        details: { ...state.details, error: null, errorCode: "" },
        analytics: { ...state.analytics, error: null, errorCode: "" },
        updateStatus: { ...state.updateStatus, error: null, errorCode: "" },
        updateFulfillment: {
          ...state.updateFulfillment,
          error: null,
          errorCode: "",
        },
        updateCutStatus: {
          ...state.updateCutStatus,
          error: null,
          errorCode: "",
        },
        manualPayment: {
          ...state.manualPayment,
          error: null,
          errorCode: "",
        },
        refundOrder: { ...state.refundOrder, error: null, errorCode: "" },
        returnList: { ...state.returnList, error: null, errorCode: "" },
        returnDetails: { ...state.returnDetails, error: null, errorCode: "" },
        approveReturn: { ...state.approveReturn, error: null, errorCode: "" },
        rejectReturn: { ...state.rejectReturn, error: null, errorCode: "" },
        markReceived: { ...state.markReceived, error: null, errorCode: "" },
        refundReturn: { ...state.refundReturn, error: null, errorCode: "" },
      };

    case actionTypes.ADMIN_ORDER_RESET_META:
      return {
        ...state,
        list: {
          ...state.list,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        details: {
          ...state.details,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        analytics: {
          ...state.analytics,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        updateStatus: {
          ...state.updateStatus,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        updateFulfillment: {
          ...state.updateFulfillment,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        updateCutStatus: {
          ...state.updateCutStatus,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        manualPayment: {
          ...state.manualPayment,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        refundOrder: {
          ...state.refundOrder,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        returnList: {
          ...state.returnList,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        returnDetails: {
          ...state.returnDetails,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        approveReturn: {
          ...state.approveReturn,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        rejectReturn: {
          ...state.rejectReturn,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        markReceived: {
          ...state.markReceived,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        refundReturn: {
          ...state.refundReturn,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
      };

    case actionTypes.ADMIN_ORDER_RESET_STATE:
      return initialState;

    default:
      return state;
  }
}

export default adminOrderReducer;
